/**
 * UsageProgressBar Component
 * Progress bar showing usage toward a limit
 */

interface UsageProgressBarProps {
  current: number
  limit: number | null // null = unlimited
  label: string
  showPercentage?: boolean
}

export function UsageProgressBar({
  current,
  limit,
  label,
  showPercentage = false
}: UsageProgressBarProps) {
  // Handle unlimited case
  if (limit === null) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-700">{label}</span>
          <span className="text-gray-600">Unlimited ∞</span>
        </div>
        <div className="h-2 rounded-full bg-gray-200">
          <div className="h-full rounded-full bg-gradient-to-r from-green-400 to-green-500 w-full" />
        </div>
      </div>
    )
  }

  const percentage = Math.min((current / limit) * 100, 100)
  const remaining = Math.max(0, limit - current)

  // Color coding based on usage
  let barColor = 'from-green-400 to-green-500' // < 50%
  if (percentage >= 80) {
    barColor = 'from-red-400 to-red-500' // >= 80%
  } else if (percentage >= 50) {
    barColor = 'from-yellow-400 to-yellow-500' // >= 50%
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-600">
          {current}/{limit} used
          {showPercentage && ` (${Math.round(percentage)}%)`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full bg-gradient-to-r transition-all duration-300 ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {remaining <= 2 && remaining > 0 && (
        <p className="text-xs text-amber-600 mt-1">
          ⚠️ {remaining} remaining
        </p>
      )}
    </div>
  )
}
